import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  inject,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { utils } from '@app/shared';
import { MessageService, MenuItem } from 'primeng/api';
import { toSignal } from '@angular/core/rxjs-interop';
import { SwitchThemeService } from '../../../../shared/components/switch-theme/switch-theme.service';
import { DialogModule } from 'primeng/dialog';
import { ToastModule, ToastCloseEvent } from 'primeng/toast';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressBarModule } from 'primeng/progressbar';
import { FileUploadModule } from 'primeng/fileupload';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { MenuModule } from 'primeng/menu';
import { ButtonProps } from 'primeng/button';
import { TicketService } from '../../core/services/ticket.service';
import { TicketAutomationService } from '../../core/automations/ticket-automation.service';
import { TicketAutomationSnapshot } from '../../core/automations/ticket-automation.model';
import { TicketAutomationConfirmComponent } from '../ticket-automation-confirm/ticket-automation-confirm.component';
import { TicketDevelopmentService } from '../../core/services/ticket-development.service';
import {
  TicketChannelNotificationService,
  TicketChangedToastData,
} from '../../core/services/ticket-channel-notification.service';
import { AIContextUtil } from '../../core/utils/ai-context.util';
import { TicketAiAnalysisComponent } from '../ticket-ai-analysis/ticket-ai-analysis.component';
import { buildTicketTimeline, mergeTimelineSources } from '../../core/utils/timeline.util';
import { TcDataService } from 'src/app/shared/services';
import {
  TicketDetail,
  TicketTimelineEntry,
  TicketComment,
  TimelineCommentData,
  TicketTask,
  TicketDocument,
  TicketLog,
  TicketChannelNotificationPayload,
} from '../../core/models/ticket.model';
import { findActiveTaskLog } from '../../core/utils/active-task-log.util';
import { TicketInfoPanelComponent } from './ticket-info-panel/ticket-info-panel.component';
import { TicketTimelineComponent } from './ticket-timeline/ticket-timeline.component';
import { TicketActionBarComponent } from './ticket-action-bar/ticket-action-bar.component';
import { TaskManagementModalComponent } from './task-management-modal/task-management-modal.component';
import { TicketDocumentationsComponent } from './ticket-documentations/ticket-documentations.component';
import { TicketDevelopmentComponent } from './ticket-development/ticket-development.component';
import { TicketQualityComponent } from './ticket-quality/ticket-quality.component';
import { TicketTasksComponent } from './ticket-tasks/ticket-tasks.component';
import { MarkdownModule } from 'ngx-markdown';
import { Subscription, take } from 'rxjs';
import { RealtimeService } from 'src/app/services/realtime.service';
import { TicketAttachmentsComponent } from '../ticket-attachments/ticket-attachments.component';

@Component({
  selector: 'app-ticket-detail-modal',
  standalone: true,
  imports: [
    TicketAiAnalysisComponent,
    CommonModule,
    FormsModule,
    DialogModule,
    ToastModule,
    SkeletonModule,
    TagModule,
    ButtonModule,
    CheckboxModule,
    ProgressBarModule,
    FileUploadModule,
    ChipModule,
    TooltipModule,
    AccordionModule,
    MenuModule,
    TicketInfoPanelComponent,
    TicketTimelineComponent,
    TicketActionBarComponent,
    TaskManagementModalComponent,
    TicketDocumentationsComponent,
    TicketTasksComponent,
    TicketDevelopmentComponent,
    TicketQualityComponent,
    MarkdownModule,
    TicketAttachmentsComponent,
    TicketAutomationConfirmComponent,
  ],
  templateUrl: './ticket-detail-modal.component.html',
  styleUrls: ['./ticket-detail-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TicketDetailModalComponent implements OnChanges, OnInit, OnDestroy {
  private readonly ticketService = inject(TicketService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly switchThemeService = inject(SwitchThemeService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly socketService = inject(RealtimeService);
  private readonly tcDataService = inject(TcDataService);
  private readonly automation = inject(TicketAutomationService);
  private readonly currentTheme = toSignal(this.switchThemeService.currentTheme, {
    initialValue: 'light',
  });

  // Canal ticket-{uuid}: inscrição ativa enquanto o modal está aberto
  private ticketChannelSubscription?: Subscription;
  /** UUID do canal ticket inscrito — usado exclusivamente no unsubscribe */
  private subscribedTicketUUID: string | null = null;
  private _pendingSocketSkips = 0;
  /**
   * Snapshot para as automações quando o refresh recarrega o chamado inteiro e o estado
   * anterior se perde. Preenchido só por um save do próprio usuário — assim a carga inicial
   * do modal nunca dispara automação.
   * Duplicado em `pages/ticket-detail/ticket-detail.component.ts`.
   */
  private pendingAutomationSnapshot: TicketAutomationSnapshot | null = null;
  refreshAfterClose = signal(false);
  /** Toast de alteração direta pendente (no máximo um por vez) — usado pelo atalho Ctrl+. */
  private pendingTicketChange: TicketChangedToastData | null = null;
  devRequirementsVersion = signal(0);
  devRevisionsVersion = signal(0);
  qualityCount = signal(0);

  canEdit = computed(() => {
    return utils.checkAccess(['Tickets'], true, true).result;
  });

  isDevCanRead = computed(() => {
    return utils.checkAccess(['Development'], true).result;
  });

  isDevCanWrite = computed(() => {
    return utils.checkAccess(['Development'], true, true).result;
  });

  isQualityCanRead = computed(() => {
    return utils.checkAccess(['TicketQuality'], true).result;
  });

  isQualityCanWrite = computed(() => {
    return utils.checkAccess(['TicketQuality'], true, true).result;
  });

  @ViewChild(TicketActionBarComponent) actionBarRef?: TicketActionBarComponent;
  @ViewChild(TicketInfoPanelComponent) infoPanelRef?: TicketInfoPanelComponent;

  @Input() ticketId: string | null = null;
  @Input() visible = false;
  /** Tarefa a destacar ao abrir (vinda da sprint); expande o painel Tarefas. */
  @Input() focusTaskId: string | null = null;
  accordionValue = signal<string[]>(['0', '1', '2']);

  onAccordionChange(value: string | number | string[] | number[] | null | undefined): void {
    const values = Array.isArray(value) ? value : value == null ? [] : [value];
    this.accordionValue.set(values.map(String));
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() ticketUpdated = new EventEmitter<TicketDetail>();

  private updatedTicketState: TicketDetail | null = null;
  ticket = signal<TicketDetail | null>(null);
  loading = signal(false);
  timelineEntries = signal<TicketTimelineEntry[]>([]);
  highlightedEntryId = signal<string | null>(null);
  showTaskModal = signal(false);
  taskProgressMap = signal<Map<string, number>>(new Map());
  uploadingAttachment = signal(false);
  maximized = signal(false);

  // Resizing columns
  private readonly centerWidthStorageKey = 'ticketDetailModalPanelWidth';
  private readonly defaultCenterWidth = '1.1fr';
  isResizing = signal(false);
  private startX = 0;
  private startCenterWidth = 0;
  private startRightWidth = 0;
  centerWidth = signal<string>(this.loadCenterWidth());
  rightWidth = signal<string>('1fr');
  gridColumns = computed(() => {
    if (this.maximized()) {
      return `320px 1.15fr 12px 1fr`; // Added 12px for resizer
    }
    return `320px ${this.centerWidth()} 12px 1fr`; // Right column is always 1fr
  });

  copyMenuItems: MenuItem[] = [
    {
      label: 'Copiar número',
      icon: 'pi pi-hashtag',
      command: () => this.copyTicketNumber(),
    },
    {
      label: 'Copiar código cliente',
      icon: 'pi pi-user',
      command: () => this.copyClientCode(),
    },
    {
      label: 'Copiar recado (Privado)',
      icon: 'pi pi-lock',
      command: () => this.copyNote(false),
    },
    {
      label: 'Copiar recado (Público)',
      icon: 'pi pi-globe',
      command: () => this.copyNote(true),
    },
    {
      label: 'Editar (Legado)',
      icon: 'pi pi-external-link',
      command: () =>
        window.open(
          window.location.origin + '/chamados/editar/' + this.ticket()?.ticket_number_teorema,
          '_blank'
        ),
    },
    {
      label: 'Copiar contexto (IA)',
      icon: 'pi pi-sparkles',
      command: () => this.copyAIContext(),
    },
  ];

  headerTitle = computed(() => {
    const t = this.ticket();
    if (!t) return 'Carregando...';
    const creationDate = t.created_at ? this.formatDateShort(t.created_at) : '';
    return creationDate
      ? `#${t.ticket_number_teorema} - ${creationDate}`
      : `#${t.ticket_number_teorema}`;
  });

  activeLogs = computed(() => {
    const t = this.ticket();
    if (!t?.ticket_logs) return [];
    return t.ticket_logs.filter(log => log.start_date && !log.end_date);
  });

  sortedDocuments = computed(() => {
    const t = this.ticket();
    if (!t?.documents || t.documents.length === 0) return [];

    // Adicionar índice original para manter ordem de inserção quando datas são iguais
    const docsWithIndex = t.documents.map((doc, index) => ({ doc, index }));

    return docsWithIndex
      .sort((a, b) => {
        const dateA = a.doc.created_at ? new Date(a.doc.created_at).getTime() : Date.now();
        const dateB = b.doc.created_at ? new Date(b.doc.created_at).getTime() : Date.now();

        // Ordenar por data (mais recentes primeiro)
        if (dateB !== dateA) {
          return dateB - dateA;
        }

        // Se datas iguais, usar índice reverso (últimos inseridos primeiro)
        return b.index - a.index;
      })
      .map(item => item.doc);
  });

  formatDateShort(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }

  ngOnInit(): void {}

  private subscribeToTicketChannel(ticketId: string): void {
    this.unsubscribeFromTicketChannel();
    this.ticketChannelSubscription = this.socketService
      .subscribeToChannel(`ticket-${ticketId}`)
      .subscribe((resp: any) => {
        try {
          const data = JSON.parse(resp.message);
          this.handleTicketChannelMessage(data);
        } catch (e) {
          console.error('Erro ao parsear mensagem do canal ticket:', e);
        }
      });
  }

  private unsubscribeFromTicketChannel(): void {
    if (this.ticketChannelSubscription) {
      this.ticketChannelSubscription.unsubscribe(); // teardown já decrementa ref count
      this.ticketChannelSubscription = undefined;
    }
    this.subscribedTicketUUID = null;
  }

  private handleTicketChannelMessage(data: TicketChannelNotificationPayload): void {
    try {
      const user = utils.getUser();
      if (!user) return;

      const bwUserCG = utils.getBwUserCompanyGroup(user);
      const correctUserId = bwUserCG?.user_id || user?.id;
      const changeType: string = data.change_type ?? 'ticket';

      if (this.bulkEditModeActive) {
        console.info(
          '[ticket-detail-modal] merge de socket recebido durante edição em lote:',
          changeType
        );
      }

      // Requisitos e commits: incrementa versão → filho recarrega via ngOnChanges
      const devFlags = TicketDevelopmentService.getVersionUpdateFlags(changeType);
      if (devFlags.requirements || devFlags.revisions) {
        if (data.changed_by_user_id === correctUserId && this._pendingSocketSkips > 0) {
          this._pendingSocketSkips--;
          return;
        }
        if (devFlags.requirements) this.devRequirementsVersion.update(v => v + 1);
        if (devFlags.revisions) this.devRevisionsVersion.update(v => v + 1);
        this.cdr.markForCheck();
        return;
      }

      if (data.changed_by_user_id !== correctUserId && data.changes) {
        if (TicketChannelNotificationService.isDirectTicketChange(changeType)) {
          // Alteração DIRETA em campos do chamado → toast com ação "Atualizar" (não bloqueia
          // a tela, já que o usuário pode estar editando algo que também foi alterado).
          this.notifyTicketChanged(data.changed_by_user_name ?? '', data.changes);
        } else {
          // Alteração INDIRETA (apontamento, comentário, anexo, tarefa) → toast personalizado
          // + som, e a timeline é atualizada mostrando o registro notificado (sem modal).
          this.notifyTicketActivity(changeType, data.changed_by_user_name ?? '', data.changes);
          this.refreshTicketPartial(changeType);
        }
        this.cdr.markForCheck();
      } else if (data.changed_by_user_id === correctUserId) {
        if (this._pendingSocketSkips > 0) {
          this._pendingSocketSkips--;
          return;
        }
        // Mesmo usuário, view diferente — refresh silencioso, sem alerta
        // Usa fetch inline para definir updatedTicketState e emitir ticketUpdated,
        // garantindo que a sprint também atualize (imediatamente e ao fechar o modal)
        //
        // Snapshot das automações ANTES do merge: este refresh pode ser o primeiro caminho a
        // trazer o estado novo (é o caso do apontamento) e então é o único com baseline limpo.
        // Complementar ao `onTimeEntryAdded` — não remover nenhum dos dois; o dedupe por chave
        // em `enqueue` garante um modal só.
        // Duplicado em `pages/ticket-detail/ticket-detail.component.ts`.
        const automationBefore = this.automation.snapshot(this.ticket());
        if (this.ticketId) {
          this.ticketService.getById(this.ticketId).subscribe({
            next: freshData => {
              if (!freshData) return;
              this.ticket.set(freshData);
              this.timelineEntries.set(buildTicketTimeline(freshData));
              this.updatedTicketState = freshData;
              this.ticketUpdated.emit(freshData);
              this.loadTaskProgress(freshData);
              this.cdr.markForCheck();
              this.automation.evaluate(automationBefore, freshData);
            },
            error: err => console.error('Erro ao sincronizar chamado via socket:', err),
          });
        }
      }
    } catch (e) {
      console.error('Erro ao processar mensagem do canal ticket:', e);
    }
  }

  /**
   * Toast personalizado (visual próprio, distinto dos toasts padrão do sistema) + som,
   * para alterações indiretas no chamado (apontamento, comentário, anexo, tarefa).
   */
  private notifyTicketActivity(changeType: string, user: string, changes: string): void {
    TicketChannelNotificationService.playActivitySound();
    this.messageService.add(
      TicketChannelNotificationService.buildActivityToastMessage(changeType, user, changes)
    );
  }

  /**
   * Atualiza parcialmente o chamado após uma alteração indireta e reconstrói a timeline,
   * fazendo o registro notificado aparecer/atualizar sem recarregar o modal inteiro.
   */
  private refreshTicketPartial(changeType: string): void {
    if (!this.ticketId) return;
    this.ticketService.getById(this.ticketId).subscribe({
      next: freshData => {
        const current = this.ticket();
        if (!current || !freshData) return;
        // As fontes da timeline vêm do util, ao lado de quem as consome; aqui ficam só os campos
        // de cabeçalho que este componente também precisa atualizar.
        const merged: TicketDetail = {
          ...mergeTimelineSources(current, freshData),
          status: freshData.status ?? current.status,
          situation: freshData.situation ?? current.situation,
          estimated_effort: freshData.estimated_effort ?? current.estimated_effort,
          expected_date: freshData.expected_date ?? current.expected_date,
        };
        this.ticket.set(merged);
        this.timelineEntries.set(buildTicketTimeline(merged));
        this.updatedTicketState = merged;
        if (changeType === 'ticket_tasks') {
          this.loadTaskProgress(freshData);
        }
        this.cdr.markForCheck();
      },
      error: err => console.error('Erro ao atualizar dados parciais do ticket:', err),
    });
  }

  /**
   * Toast de alteração DIRETA de campos do chamado, com botão de ação "Atualizar" (equivalente
   * ao antigo "Sim" do modal) — ignorar/fechar o toast equivale ao antigo "Não". Compartilha a
   * mesma pilha (`key: 'ticket-activity'`) dos toasts informativos de propósito, para formar um
   * histórico único (mais recente sempre no topo) em vez de duas pilhas sem relação visual.
   *
   * Existe no máximo UM toast de alteração direta por vez: se uma nova notificação chega
   * enquanto já existe uma pendente, ela é agregada no mesmo toast (o antigo é fechado
   * pontualmente por `id` — ver `resolveTicketChangeAt` — e um novo é adicionado no topo da
   * pilha) em vez de empilhar outro toast amarelo.
   */
  private notifyTicketChanged(user: string, changes: string): void {
    TicketChannelNotificationService.playActivitySound();
    const entry = TicketChannelNotificationService.buildTicketChangeEntry(user, changes);
    const previous = this.pendingTicketChange;

    // Sempre um objeto `data` NOVO (nunca muta o anterior in-place): o toast anterior já está
    // sendo fechado (closeToastById abaixo), e seu fechamento (a animação de saída é assíncrona)
    // vai disparar `onActivityToastClose` mais tarde. Se reaproveitássemos o mesmo objeto, esse
    // fechamento tardio marcaria `resolved` e zeraria `pendingTicketChange` mesmo com o toast
    // atual (que já teria as alterações agregadas) ainda na tela — perdendo tudo o que foi
    // agregado até ali. Por isso a versão antiga é aposentada (`resolved = true` diretamente,
    // sem chamar `onReject`) antes de trocar a referência.
    if (previous) {
      previous.resolved = true;
    }

    const data: TicketChangedToastData = {
      id: TicketChannelNotificationService.nextTicketChangedId(),
      resolved: false,
      changes: previous ? [...previous.changes, entry] : [entry],
      onAction: () => this.onTicketChangedConfirm(),
      onReject: () => this.onTicketChangedReject(),
    };

    this.pendingTicketChange = data;
    if (previous) {
      this.closeTicketChangeToastById(previous.id);
    }
    this.messageService.add(TicketChannelNotificationService.buildTicketChangedToastMessage(data));
  }

  private clearPendingTicketChange(data: TicketChangedToastData | undefined): void {
    if (this.pendingTicketChange === data) {
      this.pendingTicketChange = null;
    }
  }

  /**
   * Clique no botão "Atualizar" do toast de alteração direta — atualiza o cabeçalho do chamado
   * e fecha o toast (o refetch já reflete qualquer alteração agregada nele, não só a última).
   */
  onActivityToastActionClick(data: TicketChangedToastData | undefined): void {
    TicketChannelNotificationService.resolveTicketChanged(data, true);
    this.clearPendingTicketChange(data);
    if (data) {
      this.closeTicketChangeToastById(data.id);
    }
  }

  /**
   * Fecha UM toast de alteração direta específico (por `id`) acionando seu botão "X" nativo do
   * PrimeNG. Duas razões para não usar `messageService.clear('ticket-activity')`:
   * 1. Essa chave agora é compartilhada com os toasts informativos (comentário/apontamento/
   *    anexo/tarefa) — um `clear()` da chave inteira fecharia todos eles também.
   * 2. O `ToastItem` do PrimeNG pausa o timer de auto-fechamento no `mouseenter` e o REINICIA no
   *    `mouseleave` (`onMouseLeave` chama `initTimeout()`, que faz `visible.set(true)` de novo)
   *    — a menos que sua flag interna `isClosing` já esteja `true`, o que só acontece dentro do
   *    próprio `onCloseIconClick`. Como a animação de saída desliza o card para fora debaixo do
   *    cursor, um `clear()` disparava um `mouseleave` "acidental" que reabria o toast com um
   *    timer novo. Clicar no botão nativo passa pelo caminho que marca `isClosing` corretamente.
   * O `id` (via classe `ticket-changed-id-{id}` no `styleClass`) garante que resolvemos o botão
   * exato desse toast, não o primeiro `.ticket-activity-toast--action` que aparecer no DOM.
   */
  private closeTicketChangeToastById(id: number): void {
    const closeButton = document.querySelector<HTMLButtonElement>(
      `.ticket-activity-toast .p-toast-message.ticket-changed-id-${id} .p-toast-close-button`
    );
    closeButton?.click();
  }

  /** Fechamento do toast (manual ou por expiração) — se ainda não resolvido, conta como "ignorar". */
  onActivityToastClose(event: ToastCloseEvent): void {
    const data = event.message?.data as TicketChangedToastData | undefined;
    TicketChannelNotificationService.resolveTicketChanged(data, false);
    this.clearPendingTicketChange(data);
  }

  /**
   * Atalho de teclado (Ctrl+.) — mesmo efeito de clicar no botão do toast de alteração direta.
   * Funciona mesmo com um campo em foco, já que Ctrl+. não move cursor nem insere caractere em
   * nenhum navegador/SO.
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.pendingTicketChange || !TicketChannelNotificationService.isUpdateShortcut(event)) {
      return;
    }
    event.preventDefault();
    this.onActivityToastActionClick(this.pendingTicketChange);
  }

  /**
   * Atualiza os campos de "cabeçalho" do chamado (status, responsável, tags, datas etc.) sem
   * tocar em `details`/`tasks`/`ticket_logs`/`documents`/`documentations` e sem passar por
   * `loading` — evita destruir/recriar a barra de ação e demais painéis, o que apagaria um
   * comentário/apontamento/tarefa que o usuário estivesse compondo no momento (era o que
   * acontecia com o antigo `loadTicket()` completo, via troca do bloco `@if (loading())`).
   */
  private onTicketChangedConfirm(): void {
    if (!this.ticketId) return;
    this.ticketService.getById(this.ticketId).subscribe({
      next: freshData => {
        const current = this.ticket();
        if (!current || !freshData) return;
        const merged: TicketDetail = {
          ...freshData,
          details: current.details,
          tasks: current.tasks,
          ticket_logs: current.ticket_logs,
          documents: current.documents,
          documentations: current.documentations,
        };
        this.ticket.set(merged);
        this.timelineEntries.set(buildTicketTimeline(merged));
        this.updatedTicketState = merged;
        this.cdr.markForCheck();
      },
      error: err => console.error('Erro ao atualizar cabeçalho do chamado:', err),
    });
  }

  private onTicketChangedReject(): void {
    this.refreshAfterClose.set(true);
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing.set(true);
    this.startX = event.clientX;

    // Pegar as larguras atuais em pixels
    const body = document.querySelector('.modal-body') as HTMLElement;
    if (body) {
      const center = body.querySelector('.col-center') as HTMLElement;
      const right = body.querySelector('.col-right') as HTMLElement;
      if (center && right) {
        this.startCenterWidth = center.offsetWidth;
        this.startRightWidth = right.offsetWidth;
      }
    }

    // Adicionar listeners globais
    window.addEventListener('mousemove', this.onResizing);
    window.addEventListener('mouseup', this.onResizeStop);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  private onResizing = (event: MouseEvent): void => {
    if (!this.isResizing()) return;

    const deltaX = event.clientX - this.startX;
    let newCenterWidth = this.startCenterWidth + deltaX;

    const body = document.querySelector('.modal-body') as HTMLElement;
    if (body) {
      newCenterWidth = this.clampCenterWidth(newCenterWidth, body.clientWidth);
    } else {
      newCenterWidth = Math.max(newCenterWidth, 500);
    }

    this.centerWidth.set(`${newCenterWidth}px`);
  };

  private onResizeStop = (): void => {
    this.isResizing.set(false);
    window.removeEventListener('mousemove', this.onResizing);
    window.removeEventListener('mouseup', this.onResizeStop);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    this.persistCenterWidth(this.centerWidth());
  };

  resetResize(): void {
    this.centerWidth.set(this.defaultCenterWidth);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(this.centerWidthStorageKey);
    }
  }

  private clampCenterWidth(px: number, containerWidth: number): number {
    // Se a tela for média, diminuir o min-width das duas colunas de 500 para 400
    const minColWidth = containerWidth <= 1400 ? 400 : 500;
    const maxCenterWidth = containerWidth - 320 - 12 - minColWidth;
    return Math.min(Math.max(px, minColWidth), Math.max(maxCenterWidth, minColWidth));
  }

  private loadCenterWidth(): string {
    if (typeof window === 'undefined') return this.defaultCenterWidth;
    try {
      const stored = window.localStorage.getItem(this.centerWidthStorageKey);
      if (!stored || !stored.endsWith('px')) return this.defaultCenterWidth;
      const px = parseInt(stored, 10);
      if (Number.isNaN(px)) return this.defaultCenterWidth;
      return `${this.clampCenterWidth(px, window.innerWidth)}px`;
    } catch {
      return this.defaultCenterWidth;
    }
  }

  private persistCenterWidth(width: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this.centerWidthStorageKey, width);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasTicketIdChanged = !!changes['ticketId'];
    const hasVisibleChanged = !!changes['visible'];

    if ((hasTicketIdChanged || hasVisibleChanged) && this.ticketId && this.visible) {
      if (hasTicketIdChanged) this.qualityCount.set(0);
      this.accordionValue.set(this.focusTaskId ? ['0', '1', '2', '4'] : ['0', '1', '2']);
      this.loadTicket(this.ticketId);
    }

    if (hasVisibleChanged && !this.visible) {
      this.unsubscribeFromTicketChannel();
    }
  }

  onHide(): void {
    // Evita que o atalho Ctrl+. dispare uma atualização para um ticket que não está mais visível.
    this.pendingTicketChange = null;
    // O painel é destruído junto com o modal e volta no modo campo a campo — o switch
    // precisa acompanhar, senão reabre ligado sem o lote estar aberto.
    this.bulkEditModeActive = false;
    if (this.refreshAfterClose()) {
      this.refreshAfterClose.set(false);
      if (this.ticketId) {
        this.loadTicket(this.ticketId);
      }
      return;
    }

    this.unsubscribeFromTicketChannel();
    if (this.updatedTicketState) {
      const updated = this.updatedTicketState;
      this.updatedTicketState = null;
      this.ticketUpdated.emit(updated);
    }
    this.visibleChange.emit(false);
    this.ticket.set(null);
    this.timelineEntries.set([]);
  }

  openInNewTab(): void {
    const currentTicket = this.ticket();
    if (currentTicket && currentTicket.id) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/v3ndor/chamados', currentTicket.id])
      );
      window.open(url, '_blank');
      this.onHide();
    }
  }

  onCommentAdded(ticket: TicketDetail): void {
    this.ticket.set(ticket);

    const entries = buildTicketTimeline(ticket);
    this.timelineEntries.set(entries);
    this.updatedTicketState = ticket;
    this.ticketUpdated.emit(ticket);

    const latestComment = entries.find(entry => entry.type === 'comment');
    if (latestComment && latestComment.data) {
      const isCommentData = (data: unknown): data is TimelineCommentData => {
        return typeof data === 'object' && data !== null && 'id' in data;
      };

      if (isCommentData(latestComment.data)) {
        this.highlightedEntryId.set(latestComment.data.id);
        setTimeout(() => {
          this.highlightedEntryId.set(null);
          this.cdr.markForCheck();
        }, 3000);
      }
    }

    this.cdr.markForCheck();
  }

  onTimeEntryAdded(ticket: TicketDetail): void {
    // O apontamento manda `ticket_status_id` no start e no stop — daí a automação avaliar aqui.
    // Duplicado em `pages/ticket-detail/ticket-detail.component.ts`.
    const automationBefore = this.automation.snapshot(this.ticket());
    this.ticket.set(ticket);
    this.timelineEntries.set(buildTicketTimeline(ticket));
    this.loadTaskProgress(ticket);
    this.updatedTicketState = ticket;
    this.ticketUpdated.emit(ticket);
    this.cdr.markForCheck();
    this.automation.evaluate(automationBefore, ticket);
  }

  /**
   * Sugestão de automação aplicada — mesmo merge do `onTimeEntryAdded`, inclusive o
   * `ticketUpdated` que faz a listagem atrás do modal acompanhar.
   * Duplicado em `pages/ticket-detail/ticket-detail.component.ts`.
   */
  onAutomationApplied(tickets: TicketDetail[]): void {
    const updated = tickets[0];
    if (!updated) return;
    this.ticket.set(updated);
    this.timelineEntries.set(buildTicketTimeline(updated));
    this.updatedTicketState = updated;
    this.ticketUpdated.emit(updated);
    this.cdr.markForCheck();
  }

  /** Duplicado em `pages/ticket-detail/ticket-detail.component.ts`. */
  onAutomationApplyStarted(): void {
    this._pendingSocketSkips++;
  }

  onAttachmentAdded(ticket: TicketDetail): void {
    this.ticket.set(ticket);
    this.timelineEntries.set(buildTicketTimeline(ticket));
    this.updatedTicketState = ticket;
    this.ticketUpdated.emit(ticket);
    this.cdr.markForCheck();
  }

  onTicketUpdated(updatedInfo: Partial<TicketDetail>): void {
    const currentTicket = this.ticket();
    if (currentTicket) {
      const newTicketState = { ...currentTicket, ...updatedInfo };
      this.ticket.set(newTicketState);
      this.timelineEntries.set(buildTicketTimeline(newTicketState));
      this.updatedTicketState = newTicketState;
      this.cdr.markForCheck();
    }
  }

  private loadTicket(id: string): void {
    this.loading.set(true);
    this.pendingTicketChange = null;
    // Toda carga começa sem snapshot: só `onTicketRefreshRequired('full')` o preenche, logo
    // depois desta chamada. Assim uma carga inicial (ou uma que falhou antes) nunca avalia
    // automação com estado velho.
    // Duplicado em `pages/ticket-detail/ticket-detail.component.ts`.
    this.pendingAutomationSnapshot = null;
    this.ticketService.getById(id).subscribe({
      next: data => {
        if (data?.id) {
          this.tcDataService.addTicketToRecentlyViewedTickets(data.id);
          if (this.subscribedTicketUUID !== data.id) {
            this.subscribeToTicketChannel(data.id);
            this.subscribedTicketUUID = data.id;
          }
        }
        this.ticket.set(data);
        this.timelineEntries.set(buildTicketTimeline(data));
        this.loading.set(false);
        this.loadTaskProgress(data);

        // Só avalia se um save do próprio usuário pediu o reload.
        const automationBefore = this.pendingAutomationSnapshot;
        this.pendingAutomationSnapshot = null;
        if (automationBefore) {
          this.automation.evaluate(automationBefore, data);
        }
      },
      error: err => {
        this.pendingAutomationSnapshot = null;
        console.error('Erro ao buscar detalhes do chamado:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro de Consulta',
          detail: 'Não foi possível carregar os detalhes do chamado solicitado.',
          life: 5000,
        });
        this.loading.set(false);
        this.onHide();
      },
    });
  }

  private loadTaskProgress(ticket: TicketDetail): void {
    if (!ticket.tasks?.length) {
      this.taskProgressMap.set(new Map());
      return;
    }
    this.ticketService.getTasksProgress(ticket.id).subscribe({
      next: progressData => {
        const map = new Map<string, number>();
        ticket.tasks.forEach((task, index) => {
          const percentage = Array.isArray(progressData)
            ? (progressData[index]?.percentage ?? 0)
            : (progressData[task.id]?.percentage ?? 0);
          map.set(task.id, percentage);
        });
        this.taskProgressMap.set(map);
        this.cdr.markForCheck();
      },
      error: () => {
        this.taskProgressMap.set(new Map());
      },
    });
  }

  copyTicketNumber(): void {
    const t = this.ticket();
    if (t?.ticket_number_teorema) {
      utils.copyText(t.ticket_number_teorema);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Número do chamado copiado!',
      });
    }
  }

  copyClientCode(): void {
    const t = this.ticket();
    const customer = t?.customer as any;
    if (t?.customer_code || customer?.ref) {
      utils.copyText(t?.customer_code || customer?.ref || '');
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Código do cliente copiado!',
      });
    }
  }

  copyNote(pub: boolean): void {
    const t = this.ticket();
    if (!t) return;

    const customer = t.customer as any;
    const ticketNumber = t.ticket_number_teorema || '';
    const ticketUUID = t.id;
    const customerRef = customer?.ref || t.customer_code || '';
    const customerName = customer?.name || t.customer_name || '';
    const resume = t.summary || t.problem || '';

    const formatDateBR = (value: string | null | undefined): string | null => {
      if (!value) return null;
      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('pt-BR');
    };

    const dataStr = formatDateBR(t.created_at);
    const previsaoStr = formatDateBR(t.expected_date);

    const url = pub
      ? `https://catalogo.teorema.inf.br/chamadas/${ticketUUID}`
      : `${window.location.origin}/v3ndor/chamados/${ticketNumber}`;

    let firstLine = `REF. CHAMADA No. ${ticketNumber}`;
    if (dataStr) firstLine += ` - Data: ${dataStr}`;
    if (previsaoStr) firstLine += ` - Previsão: ${previsaoStr}`;

    let usuarioLine = `Usuário: ${t.responsible?.name || ''}`;
    if (t.executor?.name) usuarioLine += ` - Usuário Executor: ${t.executor.name}`;

    const lines = [
      firstLine,
      `Sistema: ${t.system?.description || ''}`,
      `Cliente: ${customerRef} ${customerName}`.trim(),
      ...(t.requester ? [`Solicitante: ${t.requester}`] : []),
      `Status: ${t.status?.description || ''}`,
      usuarioLine,
      `Resumo: ${resume}`,
      url,
    ];

    utils.copyText(lines.join('\n'));
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: pub ? 'Recado público copiado!' : 'Recado privado copiado!',
    });
  }


  /**
   * Recarrega o chamado depois de uma gravação da IA, sem passar por `loading`.
   * Espelhado em `pages/ticket-detail/ticket-detail.component.ts`.
   */
  refreshAfterAi(): void {
    const id = this.ticket()?.id;
    if (!id) return;
    this.ticketService
      .getById(id)
      .pipe(take(1))
      .subscribe({
        next: fresh => {
          if (!fresh || this.ticket()?.id !== fresh.id) return;
          this.ticket.set(fresh);
          this.timelineEntries.set(buildTicketTimeline(fresh));
          this.updatedTicketState = fresh;
          this.cdr.markForCheck();
        },
        error: err => console.error('Erro ao recarregar o chamado após a IA:', err),
      });
  }

  copyAIContext(): void {
    const t = this.ticket();
    if (!t) return;

    const context = AIContextUtil.buildTicketContext(t, (situation: string) =>
      this.getSituationDescription(situation)
    );
    utils.copyText(context);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Contexto para IA copiado!',
    });
  }

  getTaskProgress(taskId: string): number {
    return this.taskProgressMap().get(taskId) ?? 0;
  }

  getActiveTaskLog(task: TicketTask): TicketLog | null {
    return findActiveTaskLog(this.ticket()?.ticket_logs, task);
  }

  getProgressColor(percentage: number): string {
    if (percentage >= 100) return '#10b981';
    if (percentage >= 50) return '#3b82f6';
    if (percentage > 0) return '#f59e0b';
    return '#94a3b8';
  }

  isEntryHighlighted(entry: TicketTimelineEntry): boolean {
    // Só o card de comentário: um mesmo ticket_details pode render também um card de movimentação,
    // e os dois carregam o mesmo `data.id`.
    if (entry.type !== 'comment' || !entry.data) return false;

    // Type guard para verificar se é TimelineCommentData
    const isCommentData = (data: unknown): data is TimelineCommentData => {
      return typeof data === 'object' && data !== null && 'id' in data;
    };

    return isCommentData(entry.data) ? entry.data.id === this.highlightedEntryId() : false;
  }

  getSituationDescription(situation: string): string {
    switch (situation) {
      case 'A':
        return 'Aberto';
      case 'F':
        return 'Fechado';
      case 'C':
        return 'Cancelado';
      default:
        return situation || '—';
    }
  }

  getSituationSeverity(
    situation: string
  ): 'success' | 'danger' | 'warn' | 'info' | 'secondary' | 'contrast' {
    switch (situation) {
      case 'A':
        return 'success';
      case 'F':
        return 'danger';
      case 'C':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  getStatusBadgeVariant(description: string | undefined | null): string {
    const lower = (description ?? '').toLowerCase();
    if (/teste não ok/.test(lower)) return 'cancelled';
    if (/teste ok/.test(lower)) return 'closed';
    if (/\btestar\b/.test(lower)) return 'waiting';
    if (/aberto|novo|em andamento|in progress|desenvolvimento|análise|analise/.test(lower))
      return 'open';
    if (/fechado|encerrado|conclu[ií]do|finalizado|resolvido|closed/.test(lower)) return 'closed';
    if (/cancelado|cancelled|canceled|rejeitado/.test(lower)) return 'cancelled';
    if (/aguardando|pendente|espera|waiting|parado|suspenso/.test(lower)) return 'waiting';
    return 'default';
  }

  getStatusSeverity(
    description: string | undefined | null
  ): 'success' | 'danger' | 'warn' | 'info' | 'secondary' | 'contrast' {
    const variant = this.getStatusBadgeVariant(description);
    switch (variant) {
      case 'open':
        return 'info';
      case 'closed':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'waiting':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  getDialogStyleClass(): string {
    const baseClass = 'ticket-detail-dialog';
    const urgentClass = this.ticket()?.urgent ? 'urgent-modal' : '';
    const themeClass = this.currentTheme() === 'dark' ? 'tw-dark-theme' : 'tw-light-theme';
    return [baseClass, urgentClass, themeClass].filter(Boolean).join(' ');
  }

  getSortedTasks(): TicketTask[] {
    const tasks = this.ticket()?.tasks ?? [];
    return [...tasks];
  }

  getTotalTaskPoints(): number {
    const t = this.ticket();
    if (!t?.tasks) return 0;
    return t.tasks.reduce((sum, task) => sum + (task.estimated_effort ?? 0), 0);
  }

  getFinishedTaskPoints(): number {
    const t = this.ticket();
    if (!t?.tasks) return 0;
    return t.tasks
      .filter(task => !!task.finished)
      .reduce((sum, task) => sum + (task.estimated_effort ?? 0), 0);
  }

  getOverallTaskProgress(): number {
    const total = this.getTotalTaskPoints();
    if (total === 0) return 0;
    return Math.min(100, (this.getFinishedTaskPoints() / total) * 100);
  }

  openTaskModal(): void {
    this.showTaskModal.set(true);
  }

  isTaskToggleDisabled(task: TicketTask): boolean {
    return !this.canEdit() || (!!this.getActiveTaskLog(task) && !task.finished);
  }

  toggleTaskFinished(task: TicketTask, checked?: boolean): void {
    if (!this.canEdit()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Acesso Negado',
        detail: 'Você não tem permissão para editar chamados.',
      });
      return;
    }

    const currentTicket = this.ticket();
    if (!currentTicket?.id || !currentTicket.tasks) return;

    const taskIndex = currentTicket.tasks.findIndex(t => t.id === task.id);
    if (taskIndex === -1) return;

    // Otimista: atualiza a UI imediatamente
    const newTasks = [...currentTicket.tasks];
    const newFinishedState = typeof checked === 'boolean' ? checked : !newTasks[taskIndex].finished;

    if (newFinishedState && this.getActiveTaskLog(newTasks[taskIndex])) {
      newTasks[taskIndex] = { ...newTasks[taskIndex], finished: false };
      this.ticket.set({ ...currentTicket, tasks: newTasks });
      this.cdr.markForCheck();
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não é possível concluir a tarefa enquanto houver apontamento em aberto.',
        life: 4000,
      });
      return;
    }

    newTasks[taskIndex] = { ...newTasks[taskIndex], finished: newFinishedState };
    const optimisticTicketState: TicketDetail = { ...currentTicket, tasks: newTasks };

    this.ticket.set(optimisticTicketState);
    this.timelineEntries.set(buildTicketTimeline(optimisticTicketState));
    this.cdr.markForCheck();

    // Sincroniza com o backend
    this.ticketService
      .updateTask(currentTicket.id, task.id, { finished: newFinishedState })
      .subscribe({
        next: updatedTask => {
          this.onTicketRefreshRequired('status');
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Status da tarefa atualizado.',
            life: 3000,
          });
        },
        error: () => {
          // Reverte em caso de erro
          this.ticket.set(currentTicket);
          this.timelineEntries.set(buildTicketTimeline(currentTicket));
          this.cdr.markForCheck();
        },
      });
  }

  startTimeEntryForTask(task: TicketTask): void {
    if (task.finished && !this.getActiveTaskLog(task)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Ação bloqueada',
        detail: 'Não é possível iniciar apontamento em uma tarefa já concluída.',
        life: 4000,
      });
      return;
    }
    this.onTaskStartTimeEntry(task.id);
  }

  onTasksUpdated(tasks: TicketTask[]): void {
    const currentTicket = this.ticket();
    if (currentTicket) {
      const updatedTicket: TicketDetail = { ...currentTicket, tasks };
      this.ticket.set(updatedTicket);
      this.timelineEntries.set(buildTicketTimeline(updatedTicket));
      this.updatedTicketState = updatedTicket;
      this.loadTaskProgress(updatedTicket);
      this.cdr.markForCheck();
    }
  }

  onTicketRefreshRequired(type: 'status' | 'full'): void {
    this._pendingSocketSkips++;
    const currentTicket = this.ticket();
    if (!currentTicket) return;

    // Quem chama isto acabou de salvar algo (tarefa, por exemplo) que pode ter mexido no
    // status do chamado no backend — é o gatilho das automações para tarefas.
    // Duplicado em `pages/ticket-detail/ticket-detail.component.ts`.
    const automationBefore = this.automation.snapshot(currentTicket);

    if (type === 'full') {
      // A ordem importa: `loadTicket` zera o campo, então a atribuição vem depois dele.
      this.loadTicket(currentTicket.id);
      this.pendingAutomationSnapshot = automationBefore;
    } else {
      // Atualização parcial: status/esforço/prazos E as fontes da timeline.
      //
      // A timeline entra aqui porque quem chama este método acabou de salvar algo (tarefa, por
      // exemplo) e incrementou _pendingSocketSkips — ou seja, o eco do socket foi descartado de
      // propósito. Se este refresh não trouxer a movimentação, ela não chega por caminho nenhum:
      // foi o que fazia a troca de desenvolvedor de uma tarefa não aparecer na timeline. O
      // freshData já vem completo do getById; era só não descartá-lo.
      this.ticketService.getById(currentTicket.id).subscribe({
        next: freshData => {
          if (!freshData) return;
          this.ticket.update(t =>
            t
              ? {
                  ...mergeTimelineSources(t, freshData),
                  status: freshData.status,
                  estimated_effort: freshData.estimated_effort,
                  commercial_estimated_effort: freshData.commercial_estimated_effort,
                  expected_date: freshData.expected_date,
                  development_expected_date: freshData.development_expected_date,
                  analysis_expected_date: freshData.analysis_expected_date,
                }
              : t
          );
          const updated = this.ticket();
          if (updated) {
            this.timelineEntries.set(buildTicketTimeline(updated));
            this.updatedTicketState = updated;
            this.ticketUpdated.emit(updated);
          }
          this.cdr.markForCheck();
          this.automation.evaluate(automationBefore, this.ticket());
        },
        error: err => console.error('Erro ao atualizar status do ticket:', err),
      });
    }
  }

  onTaskStartTimeEntry(taskId: string): void {
    this.showTaskModal.set(false);
    if (this.actionBarRef) {
      this.actionBarRef.openTimeEntryModal(taskId);
    }
  }

  /**
   * Switch "Modo Edição" da barra de ações — abre o modo de edição em lote do
   * painel de informações. Duplicado em ticket-detail.component.ts (mesma
   * dívida de duplicação do ticket-detail, ver CLAUDE.md).
   */
  onBulkEditModeToggled(active: boolean): void {
    if (active) {
      this.infoPanelRef?.enterBulkEditMode();
    } else {
      this.infoPanelRef?.requestCancelBulkEdit();
    }
  }

  /**
   * Visibilidade (log-only) do modo de edição em lote — usado por
   * `handleTicketChannelMessage` para logar quando um merge de socket chega
   * enquanto o lote está aberto. Não bloqueia nada nem revalida o payload
   * antes de salvar — é uma decisão consciente para esta primeira versão
   * ("Modo Edição"), não uma solução de conflito completa. Duplicado em
   * ticket-detail.component.ts.
   */
  bulkEditModeActive = false;

  onBulkEditModeChanged(active: boolean): void {
    this.bulkEditModeActive = active;
  }

  formatDate(dateString: string | Date): string {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  }

  getDifficultyClass(difficulty: string): string {
    switch (difficulty?.toUpperCase()) {
      case 'F':
        return 'difficulty-easy';
      case 'M':
        return 'difficulty-medium';
      case 'D':
        return 'difficulty-hard';
      default:
        return 'difficulty-medium';
    }
  }

  onUploadAttachment(event: any, uploader?: any): void {
    const file = event.files[0];
    const t = this.ticket();
    if (!file || !t) return;

    this.uploadingAttachment.set(true);
    const path = `chamados/${t.ticket_number_teorema}`;

    this.ticketService.uploadAttachment(t.id, file, path).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Anexo enviado com sucesso',
        });
        this.loadTicket(t.id);
        this.uploadingAttachment.set(false);
        if (uploader) {
          uploader.clear();
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao enviar anexo',
        });
        this.uploadingAttachment.set(false);
        if (uploader) {
          uploader.clear();
        }
      },
    });
  }

  currentUserId = computed(() => {
    const user = utils.getUser();
    if (!user) return null;
    const bwUserCG = utils.getBwUserCompanyGroup(user);
    return bwUserCG?.user_id || user.id;
  });

  onAttachmentDeleted(): void {
    const ticket = this.ticket();
    if (!ticket) return;
    this._pendingSocketSkips++;
    this.loadTicket(ticket.id);
  }

  ngOnDestroy(): void {
    this.unsubscribeFromTicketChannel();
  }

  onDocumentationsUpdated(documentations: any[]): void {
    // Callback para quando documentações forem atualizadas
    // Pode ser usado para atualizar o estado do ticket ou fazer outras ações
  }

  onDevelopmentUpdated(_data: { requirements: any[]; revisions: any[] }): void {
    // O componente filho gerencia seu próprio estado; aqui apenas registramos
    // a atualização caso o modal precise reagir no futuro.
  }

  onDevelopmentRefreshRequired(_type: 'requirements' | 'revisions'): void {
    this._pendingSocketSkips++;
  }
}
